import { first_non_empty_number } from "./esigning_setup_helpers";
import {
    build_case_form_sections,
    build_case_gate_journey,
    resolve_gate_id_from_stage_label
} from "@/services/onboarding_journey_service";
import {
    build_selected_case_subtitle,
    build_signing_document,
    build_signing_filter_options,
    filter_gate_cases,
    get_current_role,
    matches_document_filter,
    matches_document_search,
    normalize_text,
    value_or_empty
} from "./esigning_setup_computed_utils";

export const DEFAULT_SIGNING_FILTER = "all";

export function build_signing_tracker_query(document) {
    const safe_document = document || {};
    const signing_tracking_payload = {
        submissionId: value_or_empty(safe_document.submission_id),
        docusealSubmissionId: value_or_empty(safe_document.docuseal_submission_id),
        documentId: value_or_empty(safe_document.id),
        title: value_or_empty(safe_document.title),
        type: value_or_empty(safe_document.type),
        dueDate: value_or_empty(safe_document.sent_on),
        status: value_or_empty(safe_document.status),
        entity_profile_id: value_or_empty(safe_document.entity_profile_id),
        entity_type_id: value_or_empty(safe_document.entity_type_id),
        application_id: value_or_empty(safe_document.application_id)
    };
    return signing_tracking_payload;
}

export function build_send_contract_payload(vm) {
    return {
        workflow_assignment_id: Number(vm.signer_configuration_summary.workflow_assignment_id),
        entity_profile_id: Number(vm.route_entity_profile_id),
        send_email: true
    };
}

export function build_sent_contract_params(vm) {
    return vm.route_entity_profile_id ? { entity_profile_id: vm.route_entity_profile_id } : {};
}

export function has_failed_response(parsed_response) {
    return parsed_response?.status && parsed_response.status !== "success";
}

export function resolve_send_contract_error(vm, error) {
    const response_payload = vm.parse_response_data(error?.response?.data);
    return response_payload?.message || error?.message || "Failed to send the document for signing.";
}

export function get_send_contract_success_message(parsed_response) {
    return parsed_response?.message || "Document sent for signing successfully.";
}

export function get_send_contract_failure_message(parsed_response) {
    return parsed_response?.message || "Failed to send the document for signing.";
}

function build_traversed_workflow_gate_ids(transition_history = []) {
    const logs = Array.isArray(transition_history) ? [...transition_history] : [];
    if (!logs.length) return [];

    const ordered_logs = logs.sort((left, right) => (
        new Date(left?.date_created || 0).getTime() - new Date(right?.date_created || 0).getTime()
    ));
    const gate_ids = [];
    const append_gate_id = function append_gate_id(status_name = "") {
        const gate_id = resolve_gate_id_from_stage_label(status_name);
        if (gate_id && !gate_ids.includes(gate_id)) {
            gate_ids.push(gate_id);
        }
    };

    append_gate_id(ordered_logs[0]?.from_status?.name || "");
    ordered_logs.forEach((log) => {
        append_gate_id(log?.to_status?.name || "");
    });
    return gate_ids;
}

export const esigning_setup_computed = {
    is_entity_user() {
        return get_current_role() === "entity";
    },
    can_manage_signing_requests() {
        return !this.is_entity_user;
    },
    route_entity_profile_id() {
        return first_non_empty_number(
            this.$route?.query?.entity_profile_id,
            this.$route?.query?.entityProfileId,
            this.$route?.params?.entity_profile_id,
            this.$route?.params?.entityProfileId,
            this.selected_case?.entity_profile_id,
            this.selected_case?.entity_profile?.id,
            this.selected_case?.profile?.entity_profile_id,
            this.selected_case?.profile?.id
        );
    },
    route_entity_type_id() {
        return first_non_empty_number(
            this.$route?.query?.entity_type_id,
            this.$route?.query?.entityTypeId,
            this.$route?.query?.journey,
            this.$route?.params?.entity_type_id,
            this.$route?.params?.entityTypeId,
            this.$route?.params?.journeyId,
            this.selected_case?.entity_type_id,
            this.selected_case?.threshold_entity_type_id_used,
            this.selected_case?.entity_type?.id,
            this.selected_case?.current_entity_type?.id
        );
    },
    route_application_id() {
        return first_non_empty_number(
            this.$route?.query?.application_id,
            this.$route?.query?.applicationId,
            this.$route?.params?.applicationId,
            this.$route?.params?.application_id,
            this.selected_case?.application_id,
            this.selected_case?.id
        );
    },
    has_signing_context() {
        return Boolean(this.route_entity_profile_id && this.route_entity_type_id);
    },
    has_selected_case() {
        return Boolean(this.selected_case?.application_id || this.selected_case?.id);
    },
    selected_case_name() {
        return normalize_text(this.selected_case?.name) || "Onboarding case";
    },
    selected_case_status() {
        return normalize_text(
            this.selected_case?.workflow_display_stage
            || this.selected_case?.current_workflow_status
            || this.selected_case?.stage
            || this.selected_case?.detail
            || this.selected_case?.activation
        ) || "E-signing";
    },
    selected_case_subtitle() {
        return build_selected_case_subtitle(this.selected_case);
    },
    traversed_workflow_gate_ids() {
        return build_traversed_workflow_gate_ids(this.workflow_transition_history);
    },
    case_journey_steps() {
        if (!this.selected_case) return [];

        const journey_steps = build_case_gate_journey(this.selected_case, "e-signing");
        const allowed_gate_ids = new Set([
            ...(Array.isArray(this.configured_gate_ids) ? this.configured_gate_ids : []),
            "e-signing"
        ]);
        const filtered_steps = journey_steps.filter((step) => (
            allowed_gate_ids.has(String(step?.gate_id || "").trim().toLowerCase())
        ));
        const visible_steps = filtered_steps.length ? filtered_steps : journey_steps;
        const esigning_index = visible_steps.findIndex((step) => String(step?.gate_id || "").trim().toLowerCase() === "e-signing");
        if (esigning_index < 0) {
            return visible_steps;
        }
        const reached_steps = visible_steps.slice(0, esigning_index + 1);

        const traversed_gate_ids = new Set(this.traversed_workflow_gate_ids);
        return reached_steps.map((step, index) => ({
            ...step,
            state: index === esigning_index
                ? "current"
                : index < esigning_index && (
                    String(step?.state || "").trim().toLowerCase() === "completed"
                    || traversed_gate_ids.has(step.gate_id)
                )
                    ? "completed"
                    : "upcoming"
        }));
    },
    historical_gate_tabs() {
        let current_index = this.case_journey_steps.findIndex((step) => step.state === "current");
        if (current_index < 0) {
            current_index = this.case_journey_steps.findIndex((step) => String(step?.gate_id || "").trim().toLowerCase() === "e-signing");
        }
        return current_index <= 0
            ? []
            : this.case_journey_steps
                .slice(0, current_index)
                .filter((step) => step.state === "completed")
                .map((step) => ({
                    id: `history-${step.gate_id}`,
                    gate_id: step.gate_id,
                    label: step.label,
                    badge: "Completed"
                }));
    },
    detail_tabs_with_history() {
        return [
            { id: "signing", label: "Required Action: Signing Review", badge: "Current" },
            { id: "application", label: "Application Reference", badge: "Reference" },
            ...this.historical_gate_tabs
        ];
    },
    detail_reference_tab() {
        return this.detail_tabs_with_history.find((tab) => /application reference/i.test(tab.label || "")) || null;
    },
    detail_history_tabs() {
        return this.detail_tabs_with_history.filter((tab) => tab.gate_id);
    },
    detail_shell_tabs() {
        return [
            { id: "action", label: "Required Action", badge: "Current" },
            { id: "reference", label: "Application Reference", badge: null },
            { id: "history", label: "Onboarding History", badge: this.detail_history_tabs.length ? String(this.detail_history_tabs.length) : null }
        ];
    },
    active_detail_shell_tab() {
        if (this.active_detail_tab === "application") {
            return "reference";
        }

        if (this.active_detail_tab === "case-history" || this.active_historical_gate_tab) {
            return "history";
        }

        return "action";
    },
    active_historical_gate_tab() {
        return this.historical_gate_tabs.find((tab) => tab.id === this.active_detail_tab) || null;
    },
    active_historical_gate_checks() {
        return this.active_historical_gate_tab ? this.historical_gate_checks_by_gate[this.active_historical_gate_tab.gate_id] || [] : [];
    },
    case_form_sections() {
        return this.selected_case ? build_case_form_sections(this.selected_case) : [];
    },
    filtered_gate_cases() {
        return filter_gate_cases(this.gate_cases, this.case_table_search);
    },
    signing_documents() {
        return (Array.isArray(this.sent_contracts) ? this.sent_contracts : []).map(build_signing_document);
    },
    filtered_signing_documents() {
        return this.signing_documents.filter((document) => (
            matches_document_filter(document, this.active_signing_filter)
            && matches_document_search(document, this.table_search)
        ));
    },
    signing_filter_options() {
        return build_signing_filter_options(this.signing_documents);
    },
    open_signing_documents_count() {
        return this.signing_documents.filter((document) => matches_document_filter(document, "open")).length;
    },
    completed_signing_documents_count() {
        return this.signing_documents.filter((document) => matches_document_filter(document, "completed")).length;
    },
    signing_overview_title() {
        const document_count = this.signing_documents.length;
        return `${document_count} ${document_count === 1 ? "contract has" : "contracts have"} been sent for signing`;
    },
    signing_overview_copy() {
        if (this.sent_contracts_loading) return "Loading sent contracts...";
        if (!this.signing_documents.length) return "No contracts have been sent for signing yet.";
        return `${this.open_signing_documents_count} still need signer action, ${this.completed_signing_documents_count} are completed.`;
    },
    ready_to_send_contract() {
        return Boolean(this.signer_configuration_summary?.workflow_assignment_id && this.route_entity_profile_id);
    }
};
